import { Trans } from '@lingui/macro'
import { ArrowBackIosOutlined } from '@mui/icons-material'
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material'
import { FundingStream } from 'app/backend/salesforce-maps/FundingStream'
import { updateCaseByFlow } from 'app/backend/selesforce-custom-queries/CaseQueries'
import { useExpressionOfInterestStatus } from 'app/hooks/useExpressionOfInterestStatus'
import { usePrequalifications } from 'app/hooks/usePrequalifications'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { FormConfigInterface } from 'app/services/sfAuth/sfData/sfForms'
import Loading from 'egret/components/EgretLoadable/Loading'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Redirect } from 'react-router-dom'
import { ButtonIcon } from '../common-components/ButtonIcon'
import ProgressSnackbar from '../common-components/CustomSnackbars'
import { constructFormAddressString } from '../forms/common/Common'
import Form, { handleFormSave, mapFormElements } from '../forms/Form'
import { FUNDING_PATHS } from './FundingConstants'

interface FundingStreamQuizProps {
  fundingStream: FundingStream
  quizForms: FormConfigInterface[]
  /** Where "Change selected fund" leads */
  backUrl: string
  /** Called with the path of the created application or prequalification */
  onSubmitted: (path: string) => void
}

export const InvalidProvinceAlert = (): JSX.Element => (
  <Alert severity="info">
    <AlertTitle>
      <Trans>Your organization home province is invalid for this application type</Trans>
    </AlertTitle>
  </Alert>
)

export const FundingStreamQuiz = ({
  fundingStream,
  quizForms,
  backUrl,
  onSubmitted
}: FundingStreamQuizProps): JSX.Element => {
  const language = useSelector<ReduxState, string>((state) => state.user.language)
  const user = useSelector<ReduxState, ReduxUser>((state) => state.user)
  const currentOrganization = useSelector<ReduxState, ReduxOrganization>(
    (state) => state.organization
  )
  const avaliableOrganizations = useSelector<ReduxState, ReduxState['avaliableOrganizations']>(
    (state) => state.avaliableOrganizations
  )
  const dispatch = useDispatch()
  const history = useHistory()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [submitting, setSubmitting] = useState<boolean>(false)

  const { query: prequalificationsQuery, prequalificationsByStream: activePrequalificationsMap } =
    usePrequalifications()
  const { query: expressionOfInterestQuery, approvedCase: approvedCaseEOI } =
    useExpressionOfInterestStatus(fundingStream.caseType)

  const hasApprovedPrequalification =
    activePrequalificationsMap[fundingStream.id] &&
    !activePrequalificationsMap[fundingStream.id].some((obj) => obj.stage !== 'Approved')
  const isPrequalification =
    Boolean(fundingStream?.prequalificationEligibilityQuiz) &&
    !hasApprovedPrequalification &&
    Boolean(fundingStream.prequalificationForm)

  const quizForm: FormConfigInterface = quizForms.find((form) =>
    isPrequalification
      ? form.id === fundingStream.prequalificationEligibilityQuiz
      : form.id === fundingStream.quiz
  )

  const submitApplicationForm = async (values): Promise<void> => {
    const approvedPrequalification = activePrequalificationsMap[fundingStream.id]?.find(
      (obj) => obj.stage === 'Approved'
    )

    setSubmitting(true)
    const creatingSnackbar = enqueueSnackbar('', {
      persist: true,
      content: () => ProgressSnackbar(<Trans>CREATING_NEW_APPLICATION_SNACKBAR</Trans>)
    })

    const connectedObjects = quizForm.config.objectsConnected ?? []
    const connectedMap = Object.fromEntries(
      connectedObjects.map((obj) => [
        obj.identId,
        {
          sfObject: {
            attributes: {
              type: obj.type
            }
          }
        }
      ])
    )

    const baseValues = {
      Opportunity: {
        AccountId: currentOrganization.id,
        RecordTypeId: fundingStream.applicationRecordType,
        Grant_Form__c: fundingStream.form,
        Funding_Stream__c: fundingStream.id,
        Eligibility_Quiz_Form__c: quizForm.id,
        Last_Modified_By_User_Date__c: moment.utc(),
        Last_Modified_By__c: user.userId,
        Pre_Qualification__c: approvedPrequalification?.id
      },
      Pre_Qualification__c: {
        Funding_Stream__c: fundingStream.id,
        Organization_Legal_Name__c: currentOrganization.id,
        RecordTypeId: fundingStream.prequalificationRecordType,
        Eligibility_Quiz_Form__c: quizForm.id
      },
      Account: {
        Id: currentOrganization.id
      }
    }

    const baseToSave = Object.fromEntries(
      connectedObjects
        .map((obj) => [obj.identId, baseValues[obj.type] || {}])
        .filter(([_, value]) => Object.keys(value).length > 0)
    )

    try {
      const result = await handleFormSave({
        values,
        elementsMap: mapFormElements(quizForm.config, language),
        utilityBag: {
          enqueueSnackbar
        },
        reduxBag: {
          dispatch,
          user,
          organization: currentOrganization,
          avaliableOrganizations
        },
        connectedMap,
        baseToSave
      })

      if (isPrequalification) {
        enqueueSnackbar(<Trans>PREQUALIFICATION_CREATED</Trans>, {
          variant: 'success'
        })
        onSubmitted(FUNDING_PATHS.PREQUALIFICATION + `/${result[0].id as string}`)
        return
      }

      const opportunityId: string = result[0].results[0].id
      if (approvedCaseEOI != null && fundingStream.requiresExpressionOfInterest) {
        try {
          await updateCaseByFlow({ opportunityId, id: approvedCaseEOI.id })
        } catch (error) {
          // The application is already created, so the user is still taken to it
          console.error('Linking the expression of interest to the application failed', error)
        }
      }

      enqueueSnackbar(<Trans>APPLICATION_CREATED_SNACKBAR</Trans>, {
        variant: 'success'
      })
      onSubmitted(FUNDING_PATHS.GRANT_APPLICATION + `/${opportunityId}`)
    } catch (error) {
      enqueueSnackbar(<Trans>ERROR_WHILE_CREATING_APPLICATION_SNACKBAR</Trans>, {
        variant: 'error'
      })
      console.error(error)
    } finally {
      closeSnackbar(creatingSnackbar)
      setSubmitting(false)
    }
  }

  if (prequalificationsQuery.isInitialLoading || expressionOfInterestQuery.isInitialLoading) {
    return <Loading atCenter />
  }

  // The fund can be opened directly by URL, so the approved expression of interest
  // has to be checked here too, not only on the button that links to it.
  if (fundingStream.requiresExpressionOfInterest) {
    if (approvedCaseEOI == null) {
      return <Redirect to={FUNDING_PATHS.APPLY} />
    }
  }

  // Safeguard for user accessing streams from disallowed provinces
  const isDisabled =
    Array.isArray(fundingStream.disableInProvinces) &&
    fundingStream.disableInProvinces.includes(currentOrganization.province)

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className="funding-program-container quiz-form-container"
    >
      <Grid item>
        <Typography className="eligibility-quiz-header">
          <Trans>CONFIRM_ELIGIBILITY_HEADER</Trans>
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            history.push(backUrl)
          }}
        >
          <ButtonIcon left IconClass={ArrowBackIosOutlined} />
          <Trans>CHANGE_SELECTED_FUND</Trans>
        </Button>
      </Grid>

      <Grid item xs>
        {isDisabled && (
          <Box sx={{ px: 10, py: 2.5 }}>
            <InvalidProvinceAlert />
          </Box>
        )}
        <Form
          mainObject={isPrequalification ? 'Pre_Qualification__c' : 'Opportunity'}
          defaultFormType="editable"
          formId={quizForm.id}
          fetchString={constructFormAddressString({
            objectsConnected: quizForm.config.objectsConnected?.filter(
              (obj) => obj.type === 'Account' || obj.type === 'Funding_Stream__c'
            ),
            ids: {
              Account: currentOrganization.id,
              Funding_Stream__c: fundingStream.id
            }
          })}
          // keepLanguageRestriction: language must not reset to its pre-form value on unmount — this page handles it.
          keepLanguageRestriction
          noDefaultSave
          forceDisabled={submitting || isDisabled}
          saveCallback={() => {
            setSubmitting(true)
          }}
        >
          {({ values, saveDisabled, submitDisabled }) => {
            return (
              <Grid
                container
                justifyContent="center"
                style={{
                  paddingBottom: 20,
                  paddingLeft: 12,
                  paddingRight: 12
                }}
              >
                <Button
                  fullWidth
                  disabled={submitDisabled || submitting}
                  variant="contained"
                  onClick={() => {
                    void submitApplicationForm(values)
                  }}
                >
                  <Trans>CREATE_APPLICATION_BUTTON</Trans>
                </Button>
              </Grid>
            )
          }}
        </Form>
      </Grid>
    </Grid>
  )
}
